import { Inject, Injectable } from '@angular/core';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CHECKOUT_CONFIG,
  CheckoutConfig,
  CheckoutRoutes,
} from '@checkout/util-models';
import { BasketFacade } from '@domgen/dgx-fe-basket';
import { PaymentType, RouterFacade } from '@domgen/dgx-fe-common';
import { ProgressionStep } from '@domgen/dgx-fe-components/progress';
import { TranslateService } from '@domgen/dgx-fe-i18n';
import { Observable, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CheckoutProgressionStepsTranslationDictionary } from '../../components/checkout-progression-steps/i18n/checkout-progression-steps-translation-dictionary.type';

export interface ViewModel {
  disableNavigation: boolean;
  progressionSteps: ProgressionStep[];
}

@Injectable()
export class CheckoutProgressionStepsComponentService {
  private readonly cardProgressionStep: ProgressionStep = {
    label: this.translateService.translate('CHECKOUT_PROGRESSION_STEP_CARD'),
    routerPath: CheckoutRoutes.Card,
  };
  private readonly directDebitProgressionStep: ProgressionStep = {
    label: this.translateService.translate(
      'CHECKOUT_PROGRESSION_STEP_DIRECT_DEBIT'
    ),
    routerPath: CheckoutRoutes.DirectDebit,
  };

  private readonly paymentMethodProgressionStep: ProgressionStep = {
    label: this.translateService.translate(
      'CHECKOUT_PROGRESSION_STEP_PAYMENT_METHOD'
    ),
    routerPath: CheckoutRoutes.PaymentMethod,
  };

  private readonly personalDetailsProgressionStep: ProgressionStep = {
    label: this.translateService.translate(
      'CHECKOUT_PROGRESSION_STEP_PERSONAL_DETAILS'
    ),
    routerPath: CheckoutRoutes.PersonalDetails,
  };

  private readonly progressionSteps$: Observable<ProgressionStep[]> =
    this.formatProgressionSteps();

  readonly vm$: Observable<ViewModel> = combineLatest({
    disableNavigation: this.disableNavigation(),
    progressionSteps: this.progressionSteps$,
  });

  constructor(
    @Inject(CHECKOUT_CONFIG) private readonly checkoutConfig: CheckoutConfig,
    private readonly basketFacade: BasketFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly routerFacade: RouterFacade,
    private readonly translateService: TranslateService<CheckoutProgressionStepsTranslationDictionary>
  ) {}

  private disableNavigation(): Observable<boolean> {
    return combineLatest([
      this.checkoutFacade.purchasing$,
      this.checkoutFacade.hasCompletedItems$,
      this.routerFacade.url$.pipe(
        map((url) => url === CheckoutRoutes.OrderConfirmation)
      ),
    ]).pipe(map((conditions: boolean[]) => conditions.some(Boolean)));
  }

  private formatProgressionSteps(): Observable<ProgressionStep[]> {
    if (this.checkoutConfig.progressionSteps) {
      return of(this.checkoutConfig.progressionSteps);
    }
    return combineLatest([
      this.basketFacade.selectedQuotesAvailablePaymentTypes$,
      this.basketFacade.selectedPaymentType$,
    ]).pipe(
      map(([paymentTypes, paymentType]) => {
        const singlePaymentType =
          Object.values(paymentTypes).filter(Boolean).length === 1;

        const progressionSteps: ProgressionStep[] = [
          {
            routerPath: '/appliance-details',
            label: this.translateService.translate(
              'CHECKOUT_PROGRESSION_STEP_APPLIANCE_DETAILS'
            ),
          },
          this.personalDetailsProgressionStep,
        ];

        if (!singlePaymentType) {
          progressionSteps.push(this.paymentMethodProgressionStep);
        }

        if (
          (singlePaymentType && paymentTypes.C) ||
          paymentType === PaymentType.Card
        ) {
          progressionSteps.push(this.cardProgressionStep);
        }

        if (
          (singlePaymentType && paymentTypes.D) ||
          paymentType == PaymentType.DirectDebit
        ) {
          progressionSteps.push(this.directDebitProgressionStep);
        }

        return progressionSteps;
      })
    );
  }
}
